import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  TelnyxErrorCode,
  TelnyxMessageDirection,
  TelnyxMessageEventType,
  TelnyxPortOutEventType,
} from "./telnyx-types";

// Define the error schema
const telnyxErrorSchema_ = z.object({
  code: createEnum(TelnyxErrorCode, { enumName: "TelnyxErrorCode" }),
  title: z.string(),
  detail: z.string(),
  meta: z.object({ url: z.string() }),
  source: z.object({ parameter: z.string(), pointer: z.string() }).optional(),
});
export const telnyxErrorSchema: z.ZodType<TelnyxError> = telnyxErrorSchema_;

export interface TelnyxError extends z.infer<typeof telnyxErrorSchema_> {}

// Define the media schema
const telnyxMediaSchema_ = z.object({
  url: z.string(),
  content_type: z.string().nullish(),
  sha256: z.string().nullish(),
  size: z.number().nullish(),
});
export const telnyxMediaSchema: z.ZodType<TelnyxMedia> = telnyxMediaSchema_;

export interface TelnyxMedia extends z.infer<typeof telnyxMediaSchema_> {}

// Define the message from schema
const telnyxMessageFromSchema_ = z.object({
  carrier: z.string(),
  line_type: z.string(),
  phone_number: z.string(),
  status: z.string().nullish(),
});
export const telnyxMessageFromSchema: z.ZodType<TelnyxMessageFrom> =
  telnyxMessageFromSchema_;

export interface TelnyxMessageFrom extends z.infer<
  typeof telnyxMessageFromSchema_
> {}

// Define the message to schema
const telnyxMessageToSchema_ = z.object({
  carrier: z.string(),
  line_type: z.string(),
  phone_number: z.string(),
  status: z.string().nullish(),
});
export const telnyxMessageToSchema: z.ZodType<TelnyxMessageTo> =
  telnyxMessageToSchema_;

export interface TelnyxMessageTo extends z.infer<
  typeof telnyxMessageToSchema_
> {}

// Define the message payload schema
const telnyxMessagePayloadSchema_ = z.object({
  completed_at: z.string().nullable(),
  cost: z
    .object({ amount: z.string().nullable(), currency: z.string().nullable() })
    .nullish(),
  cost_breakdown: z
    .object({
      carrier_fee: z.object({ amount: z.string(), currency: z.string() }),
      rate: z.object({ amount: z.string(), currency: z.string() }),
    })
    .nullish(),
  autoresponse_type: z.string().nullish(),
  direction: createEnum(TelnyxMessageDirection, {
    enumName: "TelnyxMessageDirection",
  }),
  encoding: z.string(),
  errors: z.array(telnyxErrorSchema),
  from: telnyxMessageFromSchema,
  id: z.string(),
  media: z.array(telnyxMediaSchema).nullish(),
  messaging_profile_id: z.string(),
  organization_id: z.string(),
  parts: z.number(),
  received_at: z.string(),
  record_type: z.literal("message"),
  sent_at: z.string().nullable(),
  tags: z.array(z.string()),
  text: z.string(),
  to: z.array(telnyxMessageToSchema),
  type: createEnum(["SMS", "MMS"], { enumName: "TelnyxMessageType" }),
  valid_until: z.string().nullable(),
  webhook_failover_url: z.string().nullable(),
  webhook_url: z.string(),
});
export const telnyxMessagePayloadSchema: z.ZodType<TelnyxMessagePayload> =
  telnyxMessagePayloadSchema_;

export interface TelnyxMessagePayload extends z.infer<
  typeof telnyxMessagePayloadSchema_
> {}

// Define the message webhook schema
const telnyxMessageWebhookSchema_ = z.object({
  data: z.object({
    event_type: createEnum(
      [
        TelnyxMessageEventType.MESSAGE_RECEIVED,
        TelnyxMessageEventType.MESSAGE_SENT,
        TelnyxMessageEventType.MESSAGE_FINALIZED,
      ],
      { enumName: "TelnyxMessageWebhookEventType" },
    ),
    id: z.string(),
    occurred_at: z.string(),
    payload: telnyxMessagePayloadSchema,
    record_type: z.literal("event"),
  }),
  meta: z.object({ attempt: z.number(), delivered_to: z.string() }),
});
export const telnyxMessageWebhookSchema: z.ZodType<TelnyxMessageWebhook> =
  telnyxMessageWebhookSchema_;

export interface TelnyxMessageWebhook extends z.infer<
  typeof telnyxMessageWebhookSchema_
> {}

// Port-out webhook schemas
export enum TelnyxPortOutStatus {
  PENDING = "pending",
  REJECTED_PENDING = "rejected-pending",
  REJECTED = "rejected",
  CANCELED = "canceled",
  AUTHORIZED = "authorized",
  PORTED = "ported",
}

export const telnyxPortOutPayloadSchema = z.object({
  id: z.string(), // Port-out order ID
  phone_numbers: z.array(z.string()),
  carrier_name: z.string().nullish(),
  spid: z.string().nullish(),
  rejection_reason: z.string().nullish(),
  foc_date: z.string().nullish(),
  user_id: z.string().nullish(),
  status: z.enum(TelnyxPortOutStatus),
});

export type TelnyxPortOutPayload = z.infer<typeof telnyxPortOutPayloadSchema>;

export const telnyxPortOutWebhookSchema = z.object({
  data: z.object({
    event_type: z.enum([
      TelnyxPortOutEventType.PORTOUT_STATUS_CHANGED,
      TelnyxPortOutEventType.PORTOUT_NEW_COMMENT,
      TelnyxPortOutEventType.PORTOUT_FOC_DATE_CHANGED,
    ]),
    id: z.string(),
    occurred_at: z.string(),
    payload: telnyxPortOutPayloadSchema,
    record_type: z.literal("event"),
  }),
  meta: z.object({ attempt: z.number(), delivered_to: z.string() }),
});

export type TelnyxPortOutWebhook = z.infer<typeof telnyxPortOutWebhookSchema>;

// Union type for all Telnyx webhooks
export type TelnyxWebhook = TelnyxMessageWebhook | TelnyxPortOutWebhook;
